import loadable from "@loadable/component";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as styles from "./msm-video-player.module.scss";

const ReactPlayer = loadable(() => import("react-player"));

function useVideoMeta(url) {
  const [thumbnail, setThumbnail] = useState(true);
  const [title, setTitle] = useState(null);

  useEffect(() => {
    if (!url) return;

    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
    if (ytMatch) {
      setThumbnail(`https://img.youtube.com/vi/${ytMatch[1]}/maxresdefault.jpg`);
      fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
        .then((r) => r.json())
        .then((data) => { if (data.title) setTitle(data.title); })
        .catch(() => {});
      return;
    }

    if (/vimeo\.com/.test(url)) {
      fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}&width=1280`)
        .then((r) => r.json())
        .then((data) => {
          if (data.thumbnail_url) setThumbnail(data.thumbnail_url);
          if (data.title) setTitle(data.title);
        })
        .catch(() => {});
    }
  }, [url]);

  return { thumbnail, title };
}

function MsmVideoPlayer({
  url,
  playing: playingProp = false,
  onPlay,
  borderRadius = "20px",
  ratio = null,
  autoplayOnDesktop = false,
  loop: loopProp = false,
}) {
  const wrapperRef    = useRef(null);
  const playerRef     = useRef(null);
  const hideTimerRef  = useRef(null);

  const [isDesktop,       setIsDesktop]       = useState(false);
  const [internalPlaying, setInternalPlaying] = useState(false);
  const [playerLoaded,    setPlayerLoaded]    = useState(false);
  const [playerKey,       setPlayerKey]       = useState(0);
  const [progress,        setProgress]        = useState(0);
  const [isSeeking,       setIsSeeking]       = useState(false);
  const [volume,          setVolume]          = useState(0);
  const [showControls,    setShowControls]    = useState(false);

  const { thumbnail, title } = useVideoMeta(url);
  const isAutoplay = isDesktop && autoplayOnDesktop;
  const effectiveLoop = isAutoplay || loopProp;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 992px)");
    setIsDesktop(mq.matches);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => { setInternalPlaying(playingProp); }, [playingProp]);

  useEffect(() => {
    if (isAutoplay) {
      setInternalPlaying(true);
      onPlay?.();
    }
  }, [isAutoplay]);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  // Mostra i controlli: su desktop spariscono al mouseleave, su mobile dopo 1.5s
  const handleMouseEnter = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setShowControls(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setShowControls(false);
  }, []);

  const handleTouchStart = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 1500);
  }, []);

  const handleFullscreen = async () => {
    const el = wrapperRef.current;
    const internalPlayer = playerRef.current?.getInternalPlayer?.();
    try {
      if (internalPlayer?.webkitEnterFullscreen) {
        internalPlayer.webkitEnterFullscreen(); return;
      }
      if (internalPlayer && !internalPlayer.tagName && typeof internalPlayer.requestFullscreen === 'function') {
        await internalPlayer.requestFullscreen().catch((err) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[MsmVideoPlayer] Vimeo fullscreen rejected:", err);
          }
        });
        return;
      }
      if (document.fullscreenEnabled && el?.requestFullscreen) {
        await el.requestFullscreen(); return;
      }
      if (el?.webkitRequestFullscreen) {
        el.webkitRequestFullscreen(); return;
      }
    } catch (_) { /* fullscreen non supportato */ }
  };

  const handleEnded = useCallback(() => {
    if (effectiveLoop) return;
    setInternalPlaying(false);
    setPlayerLoaded(false);
    setProgress(0);
    setPlayerKey((k) => k + 1);
  }, [effectiveLoop]);

  const handleProgressChange = (e) => {
    const fraction = parseFloat(e.target.value);
    setProgress(fraction);
    setIsSeeking(true);
    playerRef.current?.seekTo(fraction, "fraction");
  };

  const handleProgressCommit = () => setIsSeeking(false);

  const wrapperStyle = ratio
    ? { borderRadius, overflow: "hidden", width: "100%", aspectRatio: ratio, position: "relative" }
    : { borderRadius, overflow: "hidden", width: "100%", height: "100%", position: "relative" };

  const progressStyle = {
    background: `linear-gradient(to right, #fff ${progress * 100}%, rgba(255,255,255,0.35) ${progress * 100}%)`,
  };

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <ReactPlayer
        key={playerKey}
        ref={playerRef}
        url={url}
        width="100%"
        height="100%"
        playing={internalPlaying}
        muted={volume === 0}
        volume={volume}
        loop={effectiveLoop}
        light={isAutoplay ? false : thumbnail}
        onPlay={() => { setPlayerLoaded(true); setInternalPlaying(true); onPlay?.(); }}
        onClickPreview={() => { setInternalPlaying(true); onPlay?.(); }}
        onProgress={({ played }) => { if (!isSeeking) setProgress(played); }}
        onEnded={handleEnded}
        config={{
          youtube: {
            playerVars: { controls: 0, rel: 0, modestbranding: 1, iv_load_policy: 3, vq: "hd1080" },
          },
          vimeo: {
            playerOptions: {
              controls: false,
              byline: false,
              portrait: false,
              title: false,
              loop: effectiveLoop,
              playsinline: 1,
            },
          },
          file: {
            attributes: {
              playsInline: true,
            },
          },
        }}
      />
      <div
        className={styles.overlay}
        style={{ pointerEvents: playerLoaded ? "all" : "none" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onClick={() => setInternalPlaying((p) => !p)}
      >
        {title && (
          <p
            className={styles.videoTitle}
            style={{ opacity: showControls ? 1 : 0 }}
          >
            {title}
          </p>
        )}
        <div
          className={styles.controls}
          style={{ opacity: showControls ? 1 : 0 }}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={progress}
            onChange={handleProgressChange}
            onMouseUp={handleProgressCommit}
            onTouchEnd={handleProgressCommit}
            className={styles.progressInput}
            style={progressStyle}
          />
          <div className={styles.controlsRow}>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className={styles.volumeSlider}
            />
            <button
              onClick={handleFullscreen}
              className={styles.fullscreenBtn}
              aria-label="Fullscreen"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1h5M1 1v5M17 1h-5M17 1v5M1 17h5M1 17v-5M17 17h-5M17 17v-5" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MsmVideoPlayer;
